import { motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { formatCurrencyCompact, formatPercent } from '@/utils/format-currency'

const PIE_COLORS = [
  '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
  '#3b82f6', '#ec4899', '#14b8a6', '#f97316',
  '#6366f1', '#84cc16',
]

interface PieChartSectionProps {
  pieData: { name: string; value: number }[]
  dealCount: number
}

function PieTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null
  const p = payload[0]
  const total = p?.payload?.totalValue ?? 0
  const pct = total > 0 ? (p.value / total) * 100 : 0
  return (
    <div className="glass-card p-3 space-y-1" style={{ padding: 12 }}>
      <p className="text-text-primary font-semibold text-sm">{p.name}</p>
      <p className="text-text-muted text-xs">{formatCurrencyCompact(p.value)}</p>
      <p className="text-text-muted text-xs">{formatPercent(pct)} do pipeline</p>
    </div>
  )
}

export function PieChartSection({ pieData, dealCount }: PieChartSectionProps) {
  const total = pieData.reduce((s, d) => s + d.value, 0)
  const dataWithTotal = pieData.map((d) => ({ ...d, totalValue: total }))

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.35 }}
      className="glass-card space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-text-primary font-semibold text-sm flex items-center gap-2">
          <ChevronDown size={14} className="text-accent" />
          Distribuição por Etapa do Funil
        </h3>
        <span className="text-text-muted text-xs">{dealCount} deals</span>
      </div>

      <div className='h-72 min-h-72 min-w-0 w-full'>
        <ResponsiveContainer width='100%' height='100%' minWidth={0}>
          <PieChart>
            <Pie
              data={dataWithTotal}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={3}
              dataKey="value"
              stroke="none"
            >
              {dataWithTotal.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={PIE_COLORS[index % PIE_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<PieTooltip />} />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              wrapperStyle={{ fontSize: 11, color: 'var(--text-muted)' }}
              formatter={(value: string, entry: any) => (
                <span style={{ color: 'var(--text-muted)' }}>
                  {value} — {formatCurrencyCompact(entry.payload.value)}
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Stage breakdown table */}
      <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
        {pieData.map((item, i) => {
          const pct = total > 0 ? (item.value / total) * 100 : 0
          return (
            <div
              key={item.name}
              className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                />
                <span className="text-text-primary text-xs">{item.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-text-muted text-xs">{formatCurrencyCompact(item.value)}</span>
                <span className="text-text-muted text-xs w-10 text-right">{formatPercent(pct)}</span>
              </div>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}
